import { useCallback } from "react";

import { useSelector } from "react-redux";

import {
    // actions
    setFields,
    // selectors
    selectFormFields,
    type FieldData,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";

import { FIELD_TYPE_NEW_CUSTOM, UpdateContactForm } from "../../../../constants";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

const FRIENDLY_ID = "friendlyId";

const findMatchingFieldIndex = (updateFields: Array<FieldData>, nameList: Array<string>) => {
    return updateFields.findIndex((field) => {
        // quick out
        // @ts-expect-error bad types
        if (!field.name || field.name.length !== nameList.length) {
            return false;
        }

        // exhaustive check
        // @ts-expect-error bad types
        for (let i = 0; i < field.name.length; i++) {
            // @ts-expect-error bad types
            if (field.name[i] !== nameList[i]) {
                return false;
            }
        }

        // if we made it here we have a match
        return true;
    });
};

const updateFieldsArray = (updateFields: Array<FieldData>, typeName: Array<string>, value: string) => {
    const typeIndex = findMatchingFieldIndex(updateFields, typeName);

    if (typeIndex === -1) {
        // Add a new field
        updateFields.push({
            name: typeName,
            value,
        });
    } else {
        // Update the value of the existing field
        updateFields[typeIndex] = {
            ...updateFields[typeIndex],
            value,
        };
    }
};

export const removeUnwantedFields = (updateFields: Array<FieldData>) =>
    updateFields.filter((field) => ![FRIENDLY_ID, UpdateContactForm.updateContactName].includes(field.name as string));

type updateFieldsParmas = {
    updateFields: Array<FieldData>;
    eventIndex: number;
    id: string;
    attributeNameKey: string;
    attrValue: string;
    typeNameKey: string;
    typeValue: string;
    valueNameKey: string;
    nameValue: string;
};

const updateFieldValue = ({
    updateFields,
    eventIndex,
    id,
    attributeNameKey,
    attrValue,
    typeNameKey,
    typeValue,
    valueNameKey,
    nameValue,
}: updateFieldsParmas) => {
    if (eventIndex !== -1) {
        updateFields[eventIndex] = {
            ...updateFields[eventIndex],
            value: {
                ...updateFields[eventIndex].value,
                [id]: {
                    [attributeNameKey]: attrValue,
                    [typeNameKey]: typeValue,
                    [valueNameKey]: nameValue,
                },
            },
        };
    }
};

type HookProps = {
    contactAttributes: EventSchemaOptionEntry;
    id: string;
    listNameKey: string;
    attributeNameKey: string;
    typeNameKey: string;
    valueNameKey: string;
    selectionName: string[];
    valuesName: string[];
    typeName: string[];
};
export const useUpdateContactPanelItems = ({
    contactAttributes,
    id,
    listNameKey,
    attributeNameKey,
    valueNameKey,
    typeNameKey,
    selectionName,
    valuesName,
    typeName,
}: HookProps) => {
    const dispatch = useRVDispatch();

    const fields = useSelector(selectFormFields);
    // ALL OF THIS SHOULD BE REDUCER CODE BUT THE REDUCERS
    // BUILT TO OVERWRITE THE WHOLE STATE....
    const handleFieldChange = useCallback(
        (value: string, isCustom = false) => {
            const updateFields = Array.from(fields);
            let fieldType = "";
            // the fields can be an array of objects or an object
            // depending on if we are getting the data from the server or the form.
            // this is very messy. but its how it is until we can rebuild journeys
            if (typeof fields[0]?.name !== "string") {
                fieldType = contactAttributes[value]?.fieldType;

                if (isCustom) {
                    fieldType = FIELD_TYPE_NEW_CUSTOM;
                }

                const eventIndex = findMatchingFieldIndex(updateFields, selectionName);
                updateFields[eventIndex] = {
                    ...fields[eventIndex],
                    value,
                };
                updateFieldsArray(updateFields, typeName, fieldType);
                updateFieldsArray(updateFields, valuesName, "");
            } else {
                fieldType = contactAttributes[value]?.fieldType;
                if (isCustom) {
                    fieldType = FIELD_TYPE_NEW_CUSTOM;
                }

                const eventIndex = updateFields.findIndex((field) => {
                    // @ts-expect-error bad types
                    return field.name.includes(listNameKey);
                });
                updateFieldValue({
                    updateFields,
                    eventIndex,
                    id,
                    attributeNameKey,
                    attrValue: value,
                    typeNameKey,
                    typeValue: fieldType,
                    valueNameKey,
                    nameValue: "",
                });
            }
            dispatch(setFields(removeUnwantedFields(updateFields)));
        },
        [
            attributeNameKey,
            dispatch,
            contactAttributes,
            fields,
            id,
            listNameKey,
            selectionName,
            typeName,
            typeNameKey,
            valueNameKey,
            valuesName,
        ]
    );

    const handleValueChange = useCallback(
        (val: string) => {
            const updateFields = Array.from(fields);
            if (typeof fields[0]?.name !== "string") {
                updateFieldsArray(updateFields, valuesName, val);
            } else {
                const eventIndex = updateFields.findIndex((field) => {
                    // @ts-expect-error bad types
                    return field.name.includes(listNameKey);
                });
                updateFieldValue({
                    updateFields,
                    eventIndex,
                    id,
                    attributeNameKey,
                    attrValue: updateFields[eventIndex].value[id][attributeNameKey],
                    typeNameKey,
                    typeValue: updateFields[eventIndex].value[id][typeNameKey],
                    valueNameKey,
                    nameValue: val,
                });
            }
            dispatch(setFields(removeUnwantedFields(updateFields)));
        },
        [attributeNameKey, dispatch, fields, id, listNameKey, typeNameKey, valueNameKey, valuesName]
    );

    const handleTypeChange = useCallback(
        (value: string) => {
            const updateFields = Array.from(fields);
            if (typeof fields[0]?.name !== "string") {
                updateFieldsArray(updateFields, typeName, value);
            } else {
                const eventIndex = updateFields.findIndex((field) => {
                    // @ts-expect-error bad types
                    return field.name.includes(listNameKey);
                });
                updateFieldValue({
                    updateFields,
                    eventIndex,
                    id,
                    attributeNameKey,
                    attrValue: updateFields[eventIndex].value[id][attributeNameKey],
                    typeNameKey,
                    typeValue: value,
                    valueNameKey,
                    nameValue: "",
                });
            }

            dispatch(setFields(removeUnwantedFields(updateFields)));
        },
        [attributeNameKey, dispatch, fields, id, listNameKey, typeName, typeNameKey, valueNameKey]
    );

    return {
        handleFieldChange,
        handleValueChange,
        handleTypeChange,
    };
};
